import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { User } from '../../core/interfaces/user.interface';
import { CustomerDetails } from '../../models/customer.model';
import { BaseService } from '../../core/services/base.service';
import { ToastService } from '../../core/services/toast.service';
import { ClientDetailsModel } from '../../models/ClientDetails';
import { map } from 'rxjs';

@Component({
  selector: 'app-my-profile',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule
  ],
  templateUrl: './my-profile.component.html',
  styleUrls: ['./my-profile.component.scss']
})
export class MyProfileComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly baseService = inject(BaseService);
  private readonly toastService = inject(ToastService);

  readonly currentUser = this.authService.currentUser;
  readonly isLoggedIn = this.authService.isLoggedIn;

  // Customer details from API
  customerDetails = signal<CustomerDetails | null>(null);
  isLoading = signal(false);
  isUpdating = signal(false);
  private readonly _clientId = signal<number>(0);
  readonly clientId = computed(() => this._clientId());
  readonly userFullName = computed(() => {
    const user = this.currentUser();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });

  readonly userEmail = computed(() => {
    const user = this.currentUser();
    return user?.email || '';
  });

  readonly userPhone = computed(() => {
    const user = this.currentUser();
    return user?.phone || 'Not provided';
  });

  readonly userJoinDate = computed(() => {
    const user = this.currentUser();
    return user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '';
  });

  // Customer details computed properties
  readonly customerName = computed(() => {
    const customer = this.customerDetails();
    return customer?.name || '';
  });

  readonly customerEmail = computed(() => {
    const customer = this.customerDetails();
    return customer?.emailAddress || '';
  });

  readonly customerMobile = computed(() => {
    debugger;
    const customer = this.customerDetails();
    return customer?.mobileNumber ? customer.mobileNumber.toString() : '';
  });

  readonly customerAddress = computed(() => {
    const customer = this.customerDetails();
    return customer?.address || 'Not provided';
  });

  readonly customerGstNumber = computed(() => {
    const customer = this.customerDetails();
    return customer?.gstNnumber || 'Not provided';
  });

  readonly customerId = computed(() => {
    const customer = this.customerDetails();
    return customer?.customerId || '';
  });

  profileForm: FormGroup;
  isEditing = signal(false);

  constructor() {
    this.profileForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      emailAddress: ['', [Validators.required, Validators.email]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      address: [''],
      gstNnumber: ['']
    });

    // Initialize form with current user data
    this.initializeForm();
  }

  ngOnInit(): void {
    // Check if user is logged in, if not redirect to home
    if (!this.authService.isUserLoggedIn()) {
      this.authService.showLoginForm();
      this.router.navigate(['/']);
      return;
    } else {
      this.GetClient();
    }

    // Fetch customer details from API
    this.fetchCustomerDetails();
  }

  GetClient() {
    debugger;

    const apiUrl = `/api/B2COnlineBooking/B2CclientDetails`;
    this.baseService.getData(apiUrl).pipe(
      map((response: any) => {
        debugger;
        console.log('TripSearchService loadCities response:', response);
        if (response.responseCode === 'OK') {
          this._clientId.set(response.data.clientId);
        } 
      }),
    ).subscribe({
      next: (data) => {
        console.log('Client details loaded successfully');
      },
      error: (error) => {
        console.error('Failed to load client details:', error);
      }
    });
  }

  private fetchCustomerDetails(): void {
    const user = this.currentUser();
    if (!user || !user.id) {
      this.toastService.error('User information not available');
      return;
    }

    this.isLoading.set(true);
    const customerId = parseInt(user.id);
    const apiUrl = `/api/B2COnlineBooking/${customerId}/GetCustomerDetails`;

    this.baseService.getData(apiUrl).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.responseCode === 'OK' && res.data) {
          this.customerDetails.set(res.data as CustomerDetails);
          this.initializeForm();
        } else {
          this.toastService.error(res.responseMessage || 'Failed to fetch customer details');
        }
      },
      error: (error: any) => {
        this.isLoading.set(false);
        console.error('Error fetching customer details:', error);
        this.toastService.error('Failed to fetch customer details');
      }
    });
  }

  private initializeForm(): void {
    const customer = this.customerDetails();
    if (customer) {
      this.profileForm.patchValue({
        name: customer.name || '',
        emailAddress: customer.emailAddress || '',
        mobileNumber: customer.mobileNumber ? customer.mobileNumber.toString() : '',
        address: customer.address || '',
        gstNnumber: customer.gstNnumber || '',
        //password: '' // Password field is always empty for security
      });
    } else {
      // Fallback to user data from AuthService
      const user = this.currentUser();
      if (user) {
        this.profileForm.patchValue({
          name: `${user.firstName} ${user.lastName}`.trim(),
          emailAddress: user.email || '',
          mobileNumber: user.phone || '',
          address: '',
          gstNnumber: '',
          //password: ''
        });
      }
    }
  }

  startEditing(): void {
    this.isEditing.set(true);
  }

  cancelEditing(): void {
    this.isEditing.set(false);
    this.initializeForm(); // Reset to original values
  }

  // private getClientId(): number {
  //   // Get client ID from auth service
  //   debugger;
  //   const clientId = this.authService.getClientId();
  //   return clientId || 0;
  // }

  saveProfile(): void {
    debugger;
    if (this.profileForm.valid) {
      this.isUpdating.set(true);

      const formValues = this.profileForm.value;
      const clientId = this.clientId();

      if (!clientId) {
        this.toastService.error('Client information not available. Please login again.');
        this.isUpdating.set(false);
        return;
      }

      // Prepare the update payload
      const updatePayload = {
        name: formValues.name,
        clientId: clientId,
        mobileNumber: formValues.mobileNumber,
        emailAddress: formValues.emailAddress,
        address: formValues.address || '',
        password: 'test',
        gstNnumber: formValues.gstNnumber || '',
        customerId: this.customerId() || 0,
        otp: '',
        returnSuccess: true,
        returnMessage: '',
        countryCode: ''
      };

      const apiUrl = '/api/B2COnlineBooking/CustomerProfileUpdate';

      this.baseService.postData(apiUrl, updatePayload).subscribe({
        next: (res: any) => {
          this.isUpdating.set(false);

          if (res.responseCode === 'OK') {
            this.toastService.success('Profile updated successfully!');

            // Update local customer details
            if (res.data) {
              this.customerDetails.set(res.data as CustomerDetails);

              // Update localStorage with new customer details
              try {
                const existingCustomerDetails = localStorage.getItem('customerDetails');
                if (existingCustomerDetails) {
                  const existingData = JSON.parse(existingCustomerDetails);
                  const updatedData = { ...existingData, ...res.data };
                  localStorage.setItem('customerDetails', JSON.stringify(updatedData));
                }
              } catch (error) {
                console.error('Error updating localStorage:', error);
              }
            }

            this.isEditing.set(false);
            this.initializeForm(); // Reset form
          } else {
            this.toastService.error(res.responseMessage || 'Failed to update profile');
          }
        },
        error: (error: any) => {
          this.isUpdating.set(false);
          console.error('Error updating profile:', error);

          let errorMessage = 'Failed to update profile. Please try again.';
          if (error.error && error.error.responseMessage) {
            errorMessage = error.error.responseMessage;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.toastService.error(errorMessage);
        }
      });
    } else {
      this.markFormGroupTouched(this.profileForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      }
    });
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  goBack(): void {
    this.router.navigate(['/']);
  }
} 